import { defineComponent as _defineComponent } from 'vue'
import { unref as _unref, toDisplayString as _toDisplayString, createVNode as _createVNode, createTextVNode as _createTextVNode, withCtx as _withCtx, isRef as _isRef, openBlock as _openBlock, createBlock as _createBlock, createCommentVNode as _createCommentVNode, createElementVNode as _createElementVNode, withModifiers as _withModifiers, normalizeClass as _normalizeClass, createElementBlock as _createElementBlock } from "vue"

const _hoisted_1 = {
  class: "d-flex align-items-center justify-content-center",
  style: {"min-height":"200px"}
}
const _hoisted_2 = ["id"]

import { computed, ref, watch, onMounted, PropType } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import {
  BAlert,
  BButton,
  BButtonGroup,
  BCard,
  BPagination,
  BSpinner,
  BTable,
  BTooltip,
} from 'bootstrap-vue-next';
import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome';
import { useQuery } from '@vue/apollo-composable';
import { scrollToElement } from '@/utils/view-utils';
import { bucketUserRole } from '@/utils/bucket-utils';
import { deepParseDates, formatDate } from '@/utils/date-utils';
import {
  BucketsDocument,
  BucketsLiteDocument,
  BucketType,
} from '@/gql/graphql';
import {
  BUCKET_DATA_TYPES,
  PAGING_ITEMS_PER_PAGE,
  TRUNCATE_TEXT_LENGTH,
} from '@/constants';
import { useUserStore } from '@/store/user';
import { openRouterLink } from '@/utils/router-utils';
import { truncateText } from '@/utils/string-utils';

// Get the current route and router.

export default /*@__PURE__*/_defineComponent({
  __name: 'BucketList',
  props: {
  // component properties
  parentName: { type: String as PropType<string> },
  // bucket list filter
  dataType: String as PropType<string>,
  acrossUsers: { type: Boolean, default: false },
  bucketFriendlyNamePattern: {
    type: String as PropType<string | null>,
    default: null,
  },
  projectId: { type: Number as PropType<number | null>, default: null },
  // date & time filters
  createdAfter: { type: String as PropType<string | null>, default: null },
  createdBefore: { type: String as PropType<string | null>, default: null },
  // paging specification
  pagingItemsPerPage: { type: Number, default: PAGING_ITEMS_PER_PAGE },
  pagingHideControls: { type: Boolean, default: false },
  // additinal component props
  transparentStyle: { type: Boolean, default: false },
  messageNoItemsFound: {
    type: String,
    default: 'There are no data in your collection.',
  },
  showUserRole: {
    type: Boolean,
    default: true,
  },
  showBucketDescription: {
    type: Boolean,
    default: true,
  },
  queryDocument: {
    type: Object as PropType<
      typeof BucketsDocument | typeof BucketsLiteDocument
    >,
    default: BucketsDocument,
  },
},
  setup(__props, { expose: __expose }) {

const route = useRoute();
const router = useRouter();

const userStore = useUserStore();

const activeBucketId = ref<number | null>(null);
const activeBucketPathPrefix = ref('');

// --- Component Properties ---
const props = __props;

// --- Component State ---
const pagingPageCurrent = ref<number>(1);
const pagingTotalRows = computed(() => {
  return bucketsQueryResult.value?.buckets?.total || 0;
});

// When the currentPathPrefix changes, update the URL's 'path' parameter.
watch(activeBucketPathPrefix, (newPath) => {
  // Update only the 'path' parameter, preserving the rest.
  router.replace({
    query: { ...route.query, path: newPath },
  });

  // Scroll to expanded accordion item with slight delay to allow for rendering.
  scrollToElement(`bucket-title-${activeBucketId.value}`, 500);
});

// When the activeAccordion changes (i.e. user selects a different bucket),
// update the URL's 'bucket' parameter and reset the currentPathPrefix.
watch(activeBucketId, (newBucketId, oldBucketId) => {
  // Build new query parameters, always updating the bucket param.
  const newQuery = {
    ...route.query,
    path: activeBucketPathPrefix.value,
    bucket: newBucketId ? newBucketId.toString() : undefined,
  };

  // Only clear the 'path' if there was a previous bucket and it changes.
  if (oldBucketId !== null && newBucketId !== oldBucketId) {
    newQuery.path = '';
    activeBucketPathPrefix.value = '';
  }
  router.replace({ query: newQuery });

  // Scroll to expanded accordion item with slight delay to allow for rendering.
  scrollToElement(`bucket-title-${newBucketId}`, 500);
});

// When the component mounts, initialize these values from the URL query parameters.
onMounted(() => {
  // If the query parameter 'bucket' is present, use it as the active bucket id.
  if (route.query.bucket) {
    activeBucketId.value = Number(route.query.bucket) || null;
  }
  // If the query parameter 'path' is present, set it as the current folder path.
  if (route.query.path) {
    activeBucketPathPrefix.value = String(route.query.path);
  }
});

// --- Query Buckets ---
const {
  result: bucketsQueryResult,
  loading: bucketsQueryInProgress,
  error: bucketsQueryError,
  refetch: refetchBuckets,
} = useQuery(props.queryDocument, {
  acrossUsers: props.acrossUsers,
  dataType: props.dataType,
  friendlyNamePattern: props.bucketFriendlyNamePattern,
  projectId: props.projectId,
  createdAfter: props.createdAfter,
  createdBefore: props.createdBefore,
  pagingPageCurrent: pagingPageCurrent.value,
  pagingItemsPerPage: props.pagingItemsPerPage,
});

// --- Buckets ---
const buckets = computed<BucketType[]>(() => {
  const rawList = bucketsQueryResult.value?.buckets?.buckets || [];
  const filtered = rawList.filter((bucket) => bucket != null);
  return filtered.map((bucket) => deepParseDates(bucket)) as BucketType[];
});

function requestRefetchBuckets() {
  refetchBuckets({
    acrossUsers: props.acrossUsers,
    dataType: props.dataType,
    createdAfter: props.createdAfter,
    createdBefore: props.createdBefore,
    friendlyNamePattern: props.bucketFriendlyNamePattern,
    projectId: props.projectId,
    pagingPageCurrent: pagingPageCurrent.value,
    pagingItemsPerPage: props.pagingItemsPerPage,
  })
    ?.catch((err) => {
      console.error('Refetch buckets failed:', err);
    })
    ?.then(() => {
      console.debug('Refetched buckets');
    });
}

watch(pagingPageCurrent, (newPage) => {
  console.debug('page changed to', newPage);
  requestRefetchBuckets();
});

function requestSearch() {
  if (pagingPageCurrent.value !== 1) {
    pagingPageCurrent.value = 1; // this will trigger the watcher -> requestRefetchTasks
  } else {
    requestRefetchBuckets();
  }
}

// --- Expose Properties to the Outside ---
__expose({
  buckets,
  bucketsQueryInProgress,
  bucketsQueryError,
  requestSearch,
});

// --- Buckets Fields ---
const fields = computed(() => {
  let cols = [
    {
      key: 'createdAt',
      label: 'Created',
      formatter: formatDate,
      tdClass: 'align-middle',
    },
    {
      key: 'role',
      label: 'Role',
      tdClass: 'align-middle',
    },
    { key: 'friendlyName', label: 'Name', tdClass: 'align-middle' },
    { key: 'description', label: 'Description', tdClass: 'align-middle' },
    { key: 'actions', label: '', tdClass: 'align-middle text-end min-width' },
  ];

  if (!props.showUserRole) {
    cols = cols.filter((c) => c.key !== 'role');
  }

  if (!props.showBucketDescription) {
    cols = cols.filter((c) => c.key !== 'description');
  }

  return cols;
});

function handleDataRowClick(
  item: BucketType,
  index?: number,
  evt?: MouseEvent
) {
  if (item && item.id) {
    openRouterLink(
      {
        name: 'BucketDetail',
        params: { id: item.id },
        query: {
          showMetadata: '1',
          allowDownload: '1',
          listComputeTasks:
            props.dataType === BUCKET_DATA_TYPES.INPUT ? '1' : '0',
          listSamples: props.dataType === BUCKET_DATA_TYPES.INPUT ? '1' : '0',
          listProjects: props.dataType === BUCKET_DATA_TYPES.INPUT ? '1' : '0',
          listUsers: props.dataType === BUCKET_DATA_TYPES.INPUT ? '1' : '0',
          returnTo: encodeURIComponent(router.currentRoute.value.fullPath),
          returnLabel: props.parentName,
        },
      },
      router,
      evt
    );
  }
}

return (_ctx: any,_cache: any) => {
  return (_openBlock(), _createElementBlock("div", null, [
    _createVNode(_unref(BAlert), {
      modelValue: _unref(bucketsQueryError),
      "onUpdate:modelValue": _cache[0] || (_cache[0] = ($event: any) => (_isRef(bucketsQueryError) ? (bucketsQueryError).value = $event : null)),
      variant: "danger"
    }, {
      default: _withCtx(() => [
        _createTextVNode(_toDisplayString(_unref(bucketsQueryError)) + " ", 1),
        _createVNode(_unref(BButton), {
          onClick: requestRefetchBuckets,
          variant: "outline-dark",
          class: "btn-sm"
        }, {
          default: _withCtx(() => [
            _createVNode(_unref(FontAwesomeIcon), { icon: "sync" }),
            _cache[4] || (_cache[4] = _createTextVNode(" Retry ", -1))
          ]),
          _: 1
        })
      ]),
      _: 1
    }, 8, ["modelValue"]),
    (
        !props.pagingHideControls &&
        __props.pagingItemsPerPage > 0 &&
        pagingTotalRows.value > props.pagingItemsPerPage
      )
      ? (_openBlock(), _createBlock(_unref(BPagination), {
          key: 0,
          modelValue: pagingPageCurrent.value,
          "onUpdate:modelValue": _cache[1] || (_cache[1] = ($event: any) => ((pagingPageCurrent).value = $event)),
          "total-rows": pagingTotalRows.value,
          "per-page": __props.pagingItemsPerPage,
          pills: "",
          size: "sm",
          class: "mb-2"
        }, null, 8, ["modelValue", "total-rows", "per-page"]))
      : _createCommentVNode("", true),
    (!_unref(bucketsQueryError))
      ? (_openBlock(), _createBlock(_unref(BCard), {
          key: 1,
          class: _normalizeClass({ 'transparent-card': props.transparentStyle })
        }, {
          default: _withCtx(() => [
            _createVNode(_unref(BTable), {
              items: buckets.value,
              fields: fields.value,
              busy: _unref(bucketsQueryInProgress),
              onRowClicked: handleDataRowClick,
              striped: !props.transparentStyle,
              "thead-tr-class": "transparent-table",
              "tbody-tr-class": "transparent-table",
              class: _normalizeClass([
          'browser-table',
          'm-0',
          {
            'table-transparent': props.transparentStyle,
          },
        ]),
              "show-empty": "",
              small: "",
              responsive: "",
              hover: ""
            }, {
              "table-busy": _withCtx(() => [
                _createElementVNode("div", _hoisted_1, [
                  _createVNode(_unref(BSpinner), {
                    small: "",
                    class: "align-middle me-1"
                  }),
                  _cache[5] || (_cache[5] = _createElementVNode("span", { class: "ms-1" }, "Loading data...", -1))
                ])
              ]),
              empty: _withCtx(() => [...(_cache[6] || (_cache[6] = [
                _createElementVNode("div", { class: "text-center my-5" }, [
                  _createElementVNode("p", { class: "text-muted" }, "No data found.")
                ], -1)
              ]))]),
              "cell(role)": _withCtx((data) => [
                _createTextVNode(_toDisplayString(_unref(bucketUserRole)(_unref(userStore).id, data.item)), 1)
              ]),
              "cell(description)": _withCtx((data) => [
                _createElementVNode("span", {
                  id: 'bucket-description-' + data.item.id
                }, _toDisplayString(_unref(truncateText)(data.value, _unref(TRUNCATE_TEXT_LENGTH))), 9, _hoisted_2),
                (data.value && data.value.length > _unref(TRUNCATE_TEXT_LENGTH))
                  ? (_openBlock(), _createBlock(_unref(BTooltip), {
                      key: 0,
                      target: 'bucket-description-' + data.item.id,
                      class: "large-tooltip"
                    }, {
                      default: _withCtx(() => [
                        _createTextVNode(_toDisplayString(data.value), 1)
                      ]),
                      _: 2
                    }, 1032, ["target"]))
                  : _createCommentVNode("", true)
              ]),
              "cell(actions)": _withCtx((data) => [
                _createVNode(_unref(BButtonGroup), null, {
                  default: _withCtx(() => [
                    _createVNode(_unref(BButton), {
                      variant: "outline-secondary",
                      class: "btn-sm",
                      onClick: [
                        ($event: any) => (handleDataRowClick(data.item, data.item.id, $event)),
                        _cache[2] || (_cache[2] = _withModifiers(() => {}, ["stop"]))
                      ]
                    }, {
                      default: _withCtx(() => [
                        _createVNode(_unref(FontAwesomeIcon), { icon: ['fas', 'database'] })
                      ]),
                      _: 1
                    }, 8, ["onClick"])
                  ]),
                  _: 2
                }, 1024)
              ]),
              _: 1
            }, 8, ["items", "fields", "busy", "striped", "class"])
          ]),
          _: 1
        }, 8, ["class"]))
      : _createCommentVNode("", true),
    (
        !props.pagingHideControls &&
        __props.pagingItemsPerPage > 0 &&
        pagingTotalRows.value > props.pagingItemsPerPage
      )
      ? (_openBlock(), _createBlock(_unref(BPagination), {
          key: 2,
          modelValue: pagingPageCurrent.value,
          "onUpdate:modelValue": _cache[3] || (_cache[3] = ($event: any) => ((pagingPageCurrent).value = $event)),
          "total-rows": pagingTotalRows.value,
          "per-page": __props.pagingItemsPerPage,
          pills: "",
          size: "sm",
          class: "mt-2 mb-0"
        }, null, 8, ["modelValue", "total-rows", "per-page"]))
      : _createCommentVNode("", true)
  ]))
}
}

})